// src/components/InspectionHubThermographie.js
import React, { useEffect, useState } from 'react';
import './InspectionHub.css';
import inspectionStorage from '../services/inspectionStorage';
import apiService from '../services/apiService';
import cloneManager from '../services/cloneManager';
import { useRapportMeta } from '../hooks/useRapportMeta';
import Etape13 from './Etape13';
import Etape1InfosThermographieMobile from './Etape1InfosThermographieMobile';
import Etape2TableauEquipementsThermographieMobile from './Etape2TableauEquipementsThermographieMobile';
import Etape3FichesAnomaliesThermoMobile from './Etape3FichesAnomaliesThermoMobile';
import Etape4ConclusionThermoMobile from './Etape4ConclusionThermoMobile';

function InspectionHubThermographie({ mission, onBack, onGoHome }) {
  const [progress, setProgress] = useState({ completed: 0, total: 5, percentage: 0 });
  const [unsyncedChanges, setUnsyncedChanges] = useState(false);
  const [currentStep, setCurrentStep] = useState(null);
  const [stepsStatus, setStepsStatus] = useState({});
  const [isSyncing, setIsSyncing] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const { rapportTitle, applySyncResult, validateRapport, showValidateButton } = useRapportMeta(
    mission.id,
  );

  const steps = [
    { key: 'thermo_step_1', label: 'Informations générales', icon: '📋', component: Etape1InfosThermographieMobile },
    { key: 'thermo_step_2', label: 'Tableau équipements', icon: '📑', component: Etape2TableauEquipementsThermographieMobile },
    { key: 'thermo_step_3', label: 'Fiches anomalies', icon: '🔥', component: Etape3FichesAnomaliesThermoMobile },
    { key: 'thermo_step_4', label: 'Conclusion', icon: '✅', component: Etape4ConclusionThermoMobile },
    { key: 'etape_13_finalisation', label: 'Finalisation', icon: '✍️', component: Etape13 }
  ];

  useEffect(() => {
    loadProgress();
    checkUnsyncedChanges();
    loadStepsStatus();
  }, [mission.id]);

  const loadQ19Data = async () => {
    const q19 = await inspectionStorage.getStep(mission.id, 'rapport_q19_data');
    return q19 || {};
  };

  const isStepComplete = (stepKey, q19, finalisation) => {
    switch (stepKey) {
      case 'thermo_step_1':
        return !!(q19.company_name || q19.operator_name || q19.control_date);
      case 'thermo_step_2':
        return Array.isArray(q19.equipements_controles) && q19.equipements_controles.length > 0;
      case 'thermo_step_3':
        return Array.isArray(q19.fiches_anomalies) && q19.fiches_anomalies.length > 0;
      case 'thermo_step_4':
        return !!(q19.improvements_proposed || (q19.conclusion_structured?.selectedPhrases || []).length);
      case 'etape_13_finalisation':
        return !!finalisation;
      default:
        return false;
    }
  };

  const loadProgress = async () => {
    const q19 = await loadQ19Data();
    const finalisation = await inspectionStorage.getStep(mission.id, 'etape_13_finalisation');
    const completed = steps.filter(step => isStepComplete(step.key, q19, finalisation)).length;
    setProgress({
      completed,
      total: steps.length,
      percentage: Math.round((completed / steps.length) * 100)
    });
  };

  const checkUnsyncedChanges = async () => {
    const synced = await inspectionStorage.isSynced(mission.id);
    setUnsyncedChanges(!synced);
  };

  const loadStepsStatus = async () => {
    const q19 = await loadQ19Data();
    const finalisation = await inspectionStorage.getStep(mission.id, 'etape_13_finalisation');
    const status = {};
    for (const step of steps) {
      status[step.key] = isStepComplete(step.key, q19, finalisation) ? 'completed' : 'pending';
    }
    setStepsStatus(status);
  };

  const handleStepClick = (step) => {
    if (step.component) {
      setCurrentStep(step);
    } else {
      alert(`🚧 Formulaire "${step.label}" en cours de développement`);
    }
  };

  const handleBackFromStep = async () => {
    setCurrentStep(null);
    await loadProgress();
    await loadStepsStatus();
    await checkUnsyncedChanges();
  };

  const handleStepSave = async (data) => {
    console.log('✅ Étape thermo sauvegardée:', data);
    await loadProgress();
    await loadStepsStatus();
    await checkUnsyncedChanges();
  };

  const handleSync = async () => {
    try {
      const token = cloneManager.getToken();
      if (!token) {
        alert('❌ Vous devez être connecté pour synchroniser.');
        return;
      }

      setIsSyncing(true);
      const allStoredData = await inspectionStorage.getAllSteps(mission.id);
      const allData = {};
      for (const [key, value] of Object.entries(allStoredData)) {
        if (key !== 'meta' && key !== 'sync_status' && !key.includes('synced_at')) {
          allData[key] = value;
        }
      }

      if (Object.keys(allData).length === 0) {
        alert('ℹ️ Aucune donnée à synchroniser');
        setIsSyncing(false);
        return;
      }

      const result = await apiService.syncMissionData(mission.id, allData);
      if (result.success) {
        await inspectionStorage.markAsSynced(mission.id);
        await applySyncResult(result);
        setUnsyncedChanges(false);
        alert('✅ Synchronisation réussie !');
      } else {
        alert('❌ Erreur: ' + (result.error || 'Erreur inconnue'));
      }
    } catch (error) {
      console.error('❌ Erreur sync thermo:', error);
      alert('❌ Erreur de synchronisation: ' + error.message);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleValidateRapport = async () => {
    setIsValidating(true);
    try {
      const r = await validateRapport();
      if (r?.success && !r.alreadyValid) {
        alert('✅ Rapport validé. Numéro chrono enregistré.');
      }
    } finally {
      setIsValidating(false);
    }
  };

  if (currentStep && currentStep.component) {
    const StepComponent = currentStep.component;
    return (
      <StepComponent
        mission={mission}
        onBack={handleBackFromStep}
        onSave={handleStepSave}
      />
    );
  }

  return (
    <div className="inspection-hub">
      {isSyncing && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: '#10b981',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: '14px',
          fontWeight: '600'
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            border: '3px solid white',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <span>Synchronisation en cours...</span>
        </div>
      )}

      <div className="inspection-header inspection-header--inverse" style={{ background: 'linear-gradient(135deg, #7c3aed, #6d28d9)' }}>
        <button type="button" className="back-button" onClick={onBack}>
          ← Retour
        </button>
        <h1>🌡️ Thermographie</h1>
        {typeof onGoHome === 'function' ? (
          <button type="button" className="home-nav-button" onClick={onGoHome}>
            Accueil
          </button>
        ) : (
          <div />
        )}
      </div>

      <div
        className="mission-info-card"
        style={{ margin: '12px 16px 0', padding: '12px 16px' }}
      >
        <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{rapportTitle}</div>
        <p className="mission-client" style={{ margin: '6px 0 0' }}>{mission.client_nom}</p>
        {unsyncedChanges && (
          <div className="unsynced-warning" style={{ marginTop: 8 }}>
            ⚠️ Données locales non synchronisées
          </div>
        )}
      </div>

      <div className="inspection-progress">
        <div className="progress-info">
          <span className="progress-text">{progress.completed}/{progress.total} étapes</span>
          <span className="progress-percent">{progress.percentage}%</span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: progress.percentage + '%', background: '#7c3aed' }}></div>
        </div>
      </div>

      <div className="inspection-steps">
        {steps.map(step => (
          <div
            key={step.key}
            className={`step-card ${stepsStatus[step.key] === 'completed' ? 'completed' : ''}`}
            onClick={() => handleStepClick(step)}
          >
            <div className="step-icon">{step.icon}</div>
            <div className="step-info">
              <div className="step-label">{step.label}</div>
              <div className="step-status">
                {stepsStatus[step.key] === 'completed' ? '✅ Terminé' : '📝 À compléter'}
              </div>
            </div>
            <div className="step-arrow">›</div>
          </div>
        ))}
      </div>

      <div className="inspection-actions">
        {showValidateButton && (
          <button
            type="button"
            className="action-button primary"
            style={{ background: 'linear-gradient(135deg, #059669, #047857)' }}
            onClick={handleValidateRapport}
            disabled={isValidating || !navigator.onLine}
          >
            {isValidating ? 'Validation…' : 'Valider le rapport'}
          </button>
        )}
        <button className="sync-button" onClick={handleSync} disabled={isSyncing}>
          {isSyncing ? 'Sync...' : 'Sync'}
        </button>
      </div>
    </div>
  );
}

export default InspectionHubThermographie;
